import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import { X, Loader2, Crosshair, ListChecks, CalendarClock } from 'lucide-react';
import { API_BASE } from '../../config/api';
import { sanitizeHtmlWithIframes } from '../../utils/sanitizeHtml';
import {
  parseCricketChecklist,
  dueValueToDatetimeLocal,
  datetimeLocalToIsoOrNull,
  CRICKET_CARD_COVER_PRESETS,
  cardCoverHex,
} from '../../utils/cricketCardUtils';

function authHeaders(token) {
  return { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };
}

/** Itens vazios (sem texto e não marcados) não vão no PATCH — evita lixo no servidor. */
function cleanChecklistForApi(items) {
  return items
    .map((it) => ({ id: it.id, text: String(it.text || '').trim(), done: !!it.done }))
    .filter((it) => it.text.length > 0 || it.done);
}

const QUILL_MODULES = {
  toolbar: [
    [{ header: [1, 2, 3, false] }],
    ['bold', 'italic', 'underline', 'strike'],
    [{ list: 'ordered' }, { list: 'bullet' }],
    ['blockquote', 'code-block'],
    ['link', 'image', 'video'],
    ['clean'],
  ],
  clipboard: { matchVisual: false },
};

/**
 * Modal completo do cartão Cricket: título, lista, data, checklist, descrição rica (Quill).
 */
export default function CricketRichCardModal({
  open,
  card,
  sortedLists,
  layoutId,
  authToken,
  readOnly,
  onClose,
  onSaved,
  onFocusLinkedWidget,
  showToast,
  /** Quando definido, grava o cartão sem API (Cricket plano Free no navegador). */
  persistCardPatch,
}) {
  const { t } = useTranslation(['cricket']);
  const syncKeyRef = useRef('');
  const [draftTitle, setDraftTitle] = useState('');
  const [draftBody, setDraftBody] = useState('');
  const [draftListId, setDraftListId] = useState('');
  const [draftDue, setDraftDue] = useState('');
  const [draftChecklist, setDraftChecklist] = useState([]);
  const [draftCover, setDraftCover] = useState(null);
  const [saving, setSaving] = useState(false);

  const notify = useCallback(
    (msg, type) => {
      if (typeof showToast === 'function') showToast(msg, type || 'warning');
    },
    [showToast],
  );

  useEffect(() => {
    if (!open || !card) {
      if (!open) syncKeyRef.current = '';
      return;
    }
    const ck = typeof card.checklist_json === 'string' ? card.checklist_json : JSON.stringify(card.checklist_json || '');
    const key = `${card.id}|${card.updated_at ?? ''}|${card.cover_color ?? ''}|${card.title ?? ''}|${String(card.body || '').length}|${card.list_id ?? ''}|${card.due_at ?? ''}|${ck.slice(0, 160)}`;
    if (syncKeyRef.current === key) return;
    syncKeyRef.current = key;
    setDraftTitle(card.title || '');
    setDraftBody(card.body || '');
    setDraftListId(card.list_id || '');
    setDraftDue(dueValueToDatetimeLocal(card.due_at));
    setDraftChecklist(parseCricketChecklist(card.checklist_json));
    setDraftCover(cardCoverHex(card.cover_color));
  }, [open, card]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
        onClose();
      }
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [open, onClose]);

  const addChecklistItem = () => {
    setDraftChecklist((prev) => [
      ...prev,
      { id: typeof crypto !== 'undefined' && crypto.randomUUID ? crypto.randomUUID() : `t${Date.now()}`, text: '', done: false },
    ]);
  };

  const updateCheckItem = (id, patch) => {
    setDraftChecklist((prev) => prev.map((it) => (it.id === id ? { ...it, ...patch } : it)));
  };

  const removeCheckItem = (id) => {
    setDraftChecklist((prev) => prev.filter((it) => it.id !== id));
  };

  const buildPatchPayload = useCallback(
    (checklistForBody) => {
      const title = draftTitle.trim();
      const bodyHtml = sanitizeHtmlWithIframes(draftBody || '');
      const dueIso = datetimeLocalToIsoOrNull(draftDue);
      return {
        title,
        body: bodyHtml || null,
        list_id: draftListId || card.list_id,
        due_at: draftDue ? dueIso : null,
        checklist: checklistForBody.map(({ id, text, done }) => ({ id, text, done })),
        cover_color: cardCoverHex(draftCover),
      };
    },
    [draftTitle, draftBody, draftDue, draftListId, draftCover, card?.list_id],
  );

  const handleChecklistRowEnter = async (e, itemId) => {
    if (readOnly || saving) return;
    if (e.key !== 'Enter' || e.shiftKey) return;
    e.preventDefault();
    if (!card?.id) return;
    if (!persistCardPatch && !authToken) return;
    const titleTrim = draftTitle.trim();
    if (!titleTrim) {
      notify(t('cricket:cardTitleRequired'), 'warning');
      return;
    }
    const updated = draftChecklist.map((it) =>
      it.id === itemId ? { ...it, text: String(it.text || '').trim() } : it,
    );
    const forApi = cleanChecklistForApi(updated);
    const nextUi = updated.filter((it) => String(it.text || '').trim().length > 0 || it.done);
    const newId =
      typeof crypto !== 'undefined' && crypto.randomUUID ? crypto.randomUUID() : `t${Date.now()}`;
    setSaving(true);
    try {
      const payload = buildPatchPayload(forApi);
      if (typeof persistCardPatch === 'function') {
        const ok = await persistCardPatch(card.id, payload);
        if (!ok) {
          notify(t('cricket:syncError'), 'error');
          return;
        }
      } else {
        const res = await fetch(
          `${API_BASE}/layouts/${encodeURIComponent(layoutId)}/cricket/cards/${encodeURIComponent(card.id)}`,
          { method: 'PATCH', headers: authHeaders(authToken), body: JSON.stringify(payload) },
        );
        if (!res.ok) {
          notify(t('cricket:syncError'), 'error');
          return;
        }
      }
      setDraftChecklist([...nextUi, { id: newId, text: '', done: false }]);
      requestAnimationFrame(() => {
        const el = document.querySelector(`[data-cricket-checklist-input="${newId}"]`);
        if (el instanceof HTMLElement) el.focus();
      });
    } catch {
      notify(t('cricket:syncError'), 'error');
    } finally {
      setSaving(false);
    }
  };

  const save = async () => {
    if (readOnly || !card?.id) return;
    if (!persistCardPatch && !authToken) return;
    const title = draftTitle.trim();
    if (!title) {
      notify(t('cricket:cardTitleRequired'), 'warning');
      return;
    }
    setSaving(true);
    try {
      const payload = buildPatchPayload(draftChecklist.map(({ id, text, done }) => ({ id, text, done })));
      if (typeof persistCardPatch === 'function') {
        const ok = await persistCardPatch(card.id, payload);
        if (!ok) {
          notify(t('cricket:syncError'), 'error');
          return;
        }
      } else {
        const res = await fetch(
          `${API_BASE}/layouts/${encodeURIComponent(layoutId)}/cricket/cards/${encodeURIComponent(card.id)}`,
          { method: 'PATCH', headers: authHeaders(authToken), body: JSON.stringify(payload) },
        );
        if (!res.ok) {
          notify(t('cricket:syncError'), 'error');
          return;
        }
      }
      syncKeyRef.current = '';
      if (typeof onSaved === 'function') await onSaved();
      onClose();
    } catch {
      notify(t('cricket:syncError'), 'error');
    } finally {
      setSaving(false);
    }
  };

  if (!open || !card) return null;

  const coverBar = cardCoverHex(draftCover) || '#64748b';

  return createPortal(
    <div className="fixed inset-0 z-[4000] flex items-center justify-center p-3">
      <button
        type="button"
        className="absolute inset-0 bg-slate-900/50 backdrop-blur-[1px]"
        aria-label={t('cricket:closeCardModal')}
        onClick={onClose}
      />
      <div
        className="relative z-[1] flex max-h-[min(90vh,880px)] w-[min(96vw,720px)] flex-col overflow-hidden rounded-xl border border-slate-200 bg-white shadow-2xl dark:border-slate-600 dark:bg-slate-900"
        role="dialog"
        aria-modal="true"
        aria-labelledby="cricket-rich-card-title"
      >
        <div className="min-h-[40px] w-full shrink-0 rounded-t-xl" style={{ backgroundColor: coverBar }} aria-hidden />
        <div className="flex shrink-0 items-center justify-between gap-2 border-b border-slate-200 px-4 py-3 dark:border-slate-700">
          <div className="flex min-w-0 flex-1 flex-wrap items-center gap-2">
            <label className="sr-only" htmlFor="cricket-modal-list">
              {t('cricket:cardModalList')}
            </label>
            <select
              id="cricket-modal-list"
              disabled={readOnly}
              className="max-w-full rounded-lg border border-slate-200 bg-slate-50 px-2 py-1.5 text-xs font-semibold text-slate-800 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100"
              value={draftListId}
              onChange={(e) => setDraftListId(e.target.value)}
            >
              {(sortedLists || []).map((l) => (
                <option key={l.id} value={l.id}>
                  {l.title}
                </option>
              ))}
            </select>
            {!!card.widget_id && typeof onFocusLinkedWidget === 'function' && (
              <button
                type="button"
                onClick={() => onFocusLinkedWidget(card.widget_id)}
                className="inline-flex items-center gap-1 rounded-lg border border-amber-200 bg-amber-50 px-2 py-1 text-xs font-medium text-amber-900 hover:bg-amber-100 dark:border-amber-800 dark:bg-amber-950/50 dark:text-amber-200 dark:hover:bg-amber-900/40"
              >
                <Crosshair size={14} aria-hidden />
                {t('cricket:goToStageWidget')}
              </button>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg p-2 text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800"
            aria-label={t('cricket:closeCardModal')}
          >
            <X size={20} />
          </button>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto px-4 py-4">
          <label className="mb-1 block text-xs font-semibold text-slate-500 dark:text-slate-400" htmlFor="cricket-rich-card-title">
            {t('cricket:cardTitleField')}
          </label>
          <input
            id="cricket-rich-card-title"
            disabled={readOnly}
            className="mb-4 w-full rounded-lg border border-slate-200 px-3 py-2 text-lg font-semibold text-slate-900 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 dark:border-slate-600 dark:bg-slate-950 dark:text-slate-100"
            value={draftTitle}
            onChange={(e) => setDraftTitle(e.target.value)}
          />

          <div className="mb-4 flex flex-wrap gap-4">
            <div className="min-w-[200px] flex-1">
              <label className="mb-1 flex items-center gap-1 text-xs font-semibold text-slate-500 dark:text-slate-400" htmlFor="cricket-modal-due">
                <CalendarClock size={14} aria-hidden />
                {t('cricket:cardDueDate')}
              </label>
              <input
                id="cricket-modal-due"
                type="datetime-local"
                disabled={readOnly}
                className="w-full rounded-lg border border-slate-200 px-2 py-2 text-sm dark:border-slate-600 dark:bg-slate-950 dark:text-slate-100"
                value={draftDue}
                onChange={(e) => setDraftDue(e.target.value)}
              />
            </div>
            <div className="min-w-[200px] flex-1">
              <span className="mb-1 flex items-center gap-1 text-xs font-semibold text-slate-500 dark:text-slate-400">
                <span className="inline-block h-3 w-3 rounded" style={{ backgroundColor: coverBar }} aria-hidden />
                {t('cricket:changeCardColor')}
              </span>
              <div className="flex flex-wrap gap-1.5 pt-1">
                <button
                  type="button"
                  disabled={readOnly}
                  className={`h-8 w-8 rounded border text-xs font-bold ${!draftCover ? 'ring-2 ring-blue-500' : 'border-slate-300'}`}
                  onClick={() => setDraftCover(null)}
                >
                  ∅
                </button>
                {CRICKET_CARD_COVER_PRESETS.map((hex) => (
                  <button
                    key={hex}
                    type="button"
                    disabled={readOnly}
                    className={`h-8 w-8 rounded border border-black/10 ${draftCover === hex ? 'ring-2 ring-blue-500 ring-offset-1' : ''}`}
                    style={{ backgroundColor: hex }}
                    onClick={() => setDraftCover(cardCoverHex(hex) || hex)}
                  />
                ))}
              </div>
            </div>
          </div>

          <div className="mb-4">
            <div className="mb-2 flex items-center gap-2 text-xs font-semibold text-slate-500 dark:text-slate-400">
              <ListChecks size={14} aria-hidden />
              {t('cricket:cardChecklist')}
            </div>
            <div className="space-y-2">
              {draftChecklist.map((item) => (
                <div key={item.id} className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    disabled={readOnly}
                    checked={item.done}
                    onChange={(e) => updateCheckItem(item.id, { done: e.target.checked })}
                    className="h-4 w-4 shrink-0 rounded border-slate-300"
                  />
                  <input
                    type="text"
                    disabled={readOnly || saving}
                    data-cricket-checklist-input={item.id}
                    className="min-w-0 flex-1 rounded border border-slate-200 px-2 py-1.5 text-sm dark:border-slate-600 dark:bg-slate-950 dark:text-slate-100"
                    value={item.text}
                    placeholder={t('cricket:checklistItemPlaceholder')}
                    onChange={(e) => updateCheckItem(item.id, { text: e.target.value })}
                    onKeyDown={(e) => void handleChecklistRowEnter(e, item.id)}
                  />
                  {!readOnly && (
                    <button
                      type="button"
                      className="shrink-0 rounded px-2 py-1 text-xs text-red-600 hover:bg-red-50 dark:hover:bg-red-950/40"
                      onClick={() => removeCheckItem(item.id)}
                    >
                      {t('cricket:removeChecklistItem')}
                    </button>
                  )}
                </div>
              ))}
              {!readOnly && (
                <button
                  type="button"
                  onClick={addChecklistItem}
                  className="text-sm font-medium text-blue-600 hover:underline dark:text-blue-400"
                >
                  {t('cricket:addChecklistItem')}
                </button>
              )}
            </div>
          </div>

          <div className="mb-2 text-xs font-semibold text-slate-500 dark:text-slate-400">{t('cricket:cardDescription')}</div>
          <div className="cricket-rich-quill rounded-lg border border-slate-200 dark:border-slate-600 [&_.ql-container]:min-h-[220px] [&_.ql-editor]:min-h-[200px]">
            {readOnly ? (
              <div
                className="prose prose-sm max-w-none p-3 dark:prose-invert"
                dangerouslySetInnerHTML={{ __html: sanitizeHtmlWithIframes(draftBody || '') || `<p class="text-slate-400">${t('cricket:emptyDescription')}</p>` }}
              />
            ) : (
              <ReactQuill theme="snow" value={draftBody} onChange={setDraftBody} modules={QUILL_MODULES} />
            )}
          </div>
        </div>

        <div className="flex shrink-0 flex-wrap items-center justify-end gap-2 border-t border-slate-200 px-4 py-3 dark:border-slate-700">
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-800"
          >
            {t('cricket:cancelComposer')}
          </button>
          {!readOnly && (
            <button
              type="button"
              disabled={saving}
              onClick={() => save()}
              className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-60"
            >
              {saving && <Loader2 className="h-4 w-4 animate-spin" aria-hidden />}
              {t('cricket:saveCardModal')}
            </button>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
}
